import { useCallback } from "react";
import { useQuery } from "@tanstack/react-query";

import { supabase } from "@/integrations/supabase/client";
import { isNetworkError, reportError } from "@/lib/errorReporting";
import { useTenant } from "@/hooks/useTenant";
import { resolveStorageUrl, transformImage, type TransformOpts } from "@/lib/imageUrl";
import {
  areaAssetKey,
  pickSiteAssetRow,
  resolveSiteAssetSpec,
  type SiteAssetSpec,
} from "@/lib/siteAssets";

// Bulk-fetch every site_assets row once per session. The table is
// small (<100 rows expected over the life of the product) so loading
// the whole catalog at app boot is cheaper than per-asset queries.

export interface SiteAssetRow {
  id: string;
  key: string;
  tenant_id: string | null;
  media_type: "image" | "video";
  storage_path: string;
  alt: string | null;
  original_size_bytes: number | null;
  original_width: number | null;
  original_height: number | null;
  created_at: string;
  updated_at: string;
}

export function useSiteAssets() {
  return useQuery({
    queryKey: ["site_assets"],
    queryFn: async (): Promise<SiteAssetRow[]> => {
      const { data, error } = await supabase
        .from("site_assets" as never)
        .select("*")
        // Newest first: even a consumer that does a plain find() sees the
        // latest upload when duplicates exist.
        .order("updated_at", { ascending: false });
      if (error) {
        console.warn("[useSiteAssets] failed to fetch", error);
        // Reported so ops-health sees it (a missing table once hid every copy edit);
        // a dropped connection is not a bug and is not reported.
        if (!isNetworkError(error.message ?? "")) reportError("error", `site_assets read failed: ${error.code ?? ""} ${error.message}`, { source: "useSiteAssets" });
        return [];
      }
      return (data ?? []) as unknown as SiteAssetRow[];
    },
    // Site assets change rarely; stale-while-revalidate for an hour.
    staleTime: 60 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

export interface ResolvedAsset {
  /** Either `src` (live row) OR `srcSet` (fallback bundle) is set. */
  src?: string;
  /** The untransformed public URL of the upload. Derive other sizes from
   *  it with transformImage() (e.g. a 1200×630 share variant). */
  objectUrl?: string;
  srcSet?: string;
  sizes?: string;
  alt: string;
  spec: SiteAssetSpec;
  /** True when serving the bundled fallback because no admin upload
   *  exists for this key. Consumers can use this to render a "edit
   *  in admin" prompt when in admin mode. */
  isFallback: boolean;
  /** 'image' or 'video', matches the spec.mediaType but exposed
   *  here for consumers that need to branch on what to render. */
  mediaType: "image" | "video";
}

export type SiteAssetOpts = Pick<TransformOpts, "width" | "height" | "format" | "resize">;

// Site assets live inside the existing public `property-images` bucket
// under a `_site/` prefix. The Lovable Cloud workspace blocks creating
// new public buckets (`public_buckets_blocked`), so we piggyback on a
// bucket that's already public rather than introduce signed URLs.
/** Public URL of an uploaded site asset, as uploaded (no transform). */
export const siteAssetObjectUrl = (storagePath: string): string =>
  resolveStorageUrl(`property-images/${encodeURI(storagePath)}`);

/** A cover-cropped preview of an uploaded site asset for admin thumbnails. */
export const siteAssetPreviewUrl = (storagePath: string, width: number, height?: number): string =>
  transformImage(siteAssetObjectUrl(storagePath), { width, height, format: "webp" });

const unknownSpec = (key: string): SiteAssetSpec => ({
  key,
  label: key,
  page: "?",
  description: "",
  mediaType: "image",
  recommendation: { width: 800, height: 600, maxBytes: 200_000, formats: [] },
  fallback: "",
});

/**
 * Pure resolver behind the hooks: per-tenant override → global row →
 * fallback. Images go through the on-the-fly transformer; video and "raw"
 * assets (SVG logos, favicons) are served exactly as uploaded.
 *
 * Never throws, a missing row, RLS hiccup or unknown key degrades to a
 * fallback so a page never crashes over a picture.
 */
export function resolveSiteAsset(
  rows: readonly SiteAssetRow[] | undefined,
  tenantId: string | null,
  key: string,
  opts: SiteAssetOpts = {},
): ResolvedAsset {
  const spec = resolveSiteAssetSpec(key);
  if (!spec) {
    // Don't throw in production, log and return a no-op asset so a
    // typo'd key doesn't crash the page.
    console.error(`[useSiteAsset] Unknown asset key: ${key}`);
    return { src: "/placeholder.svg", alt: "", spec: unknownSpec(key), isFallback: true, mediaType: "image" };
  }

  // Prefer the tenant-specific row; fall back to the global (NULL tenant_id)
  // one. Newest row wins within each scope.
  const row = pickSiteAssetRow(rows, key, tenantId) ?? pickSiteAssetRow(rows, key, null);
  if (!row) {
    return { alt: spec.label, spec, isFallback: true, mediaType: spec.mediaType };
  }

  const objectUrl = siteAssetObjectUrl(row.storage_path);
  const src =
    row.media_type === "video" || spec.raw
      ? objectUrl
      : transformImage(objectUrl, {
          width: opts.width ?? spec.recommendation.width,
          height: opts.height,
          format: opts.format,
          resize: opts.resize,
        });

  return { src, objectUrl, alt: row.alt ?? spec.label, spec, isFallback: false, mediaType: row.media_type };
}

/** Resolve one site asset for the current tenant. */
export function useSiteAsset(key: string, opts: SiteAssetOpts = {}): ResolvedAsset {
  const tenant = useTenant();
  const { data: rows } = useSiteAssets();
  return resolveSiteAsset(rows, tenant.id, key, opts);
}

/** One hook call, many keys, for lists (area cards) where a hook per item
 *  is not possible. */
export function useSiteAssetResolver(): (key: string, opts?: SiteAssetOpts) => ResolvedAsset {
  const tenant = useTenant();
  const { data: rows } = useSiteAssets();
  return useCallback(
    (key: string, opts: SiteAssetOpts = {}) => resolveSiteAsset(rows, tenant.id, key, opts),
    [rows, tenant.id],
  );
}

export interface AreaImage {
  src: string;
  /** True when showing the bundled photo / placeholder from src/data/areas.ts. */
  isFallback: boolean;
  objectUrl?: string;
}

/** The picture for an area: the city's upload when there is one, else the
 *  bundled default passed in. */
export function areaImageSrc(
  resolve: (key: string, opts?: SiteAssetOpts) => ResolvedAsset,
  slug: string,
  fallback: string,
  opts: SiteAssetOpts = {},
): AreaImage {
  const asset = resolve(areaAssetKey(slug), opts);
  return asset.isFallback || !asset.src
    ? { src: fallback, isFallback: true }
    : { src: asset.src, isFallback: false, objectUrl: asset.objectUrl };
}
